









































































































































import {namespace} from 'vuex-class';
import {
  Component,
  Inject,
  Prop,
  Watch
} from 'vue-property-decorator';
import {CART, LABEL_SELECTION} from '@/store/types';
import {ConfigService} from '@/services/ConfigService';
import {UnitDefinition} from '@/services/ProductDeserializer';
import UnitSelector from '@/common/UnitSelector.vue';
import AmountSelector from '@/common/AmountSelector.vue';
import OrderTemplateSelection from '@/common/OrderTemplateSelection.vue';
import LabelSelection from '@/common/LabelSelection.vue';
import EssenceDropdown from '@/common/EssenceDropdown.vue';
import ProductBadges from './ProductBadges.vue';
import EssenceHideButton from '@/essential/EssenceHideButton.vue';
import {
  EssenceItemDto,
  NaraDto,
  OrderTemplateDto,
  OrderTemplateItemDto,
  OrderTemplatesApi,
  PriceDto,
  ProductAttributeInfo,
  ProductBasePrice, ProductDto,
  ProductNetFillQuantity,
  SalesConditionInfo
} from '@eurogast/hub-client-typescript';
import {ApiService} from '@/services/ApiService';
import {LockService} from '@/services/LockService';
import ProductComponentBase from '@/common/ProductComponentBase';
import {LinkUtil} from '@/services/LinkUtil';
import {Cart, CartItem} from '@/services/cart/model/cart';
import {CartEvents} from '@/services/cart/model/cart-events';
import {DependencyService} from '@/services/DependencyService';
import MatomoContext from '@/matomo/domain/MatomoContext';
import MatomoEvents from '@/matomo/events/MatomoEvents';
import {OrderTemplateEvents} from '@/domain/templates/OrderTemplateEvents';
import AddToCartButton from '@/common/cart-button/AddToCartButton.vue';
import CartButtonMessage from '@/common/cart-button/CartButtonMessage.vue';
import { CONVI_KEY } from '@/services/product/ProductService';

const cart = namespace(CART);
const label = namespace(LABEL_SELECTION);

@Component({
  components: {
    CartButtonMessage,
    AddToCartButton,
    ProductBadges,
    AmountSelector,
    UnitSelector,
    OrderTemplateSelection,
    EssenceDropdown,
    EssenceHideButton,
    LabelSelection
  }
})
export default class ResultShoppingCartControl extends ProductComponentBase {

  get containerStyle(): string {
    const baseClass = 'sr-result';
    const hideEssenceClass = this.showEssenceButton ? '' : 'sr-with-essence';
    const isInCartClass = this.inCart ? 'selected' : '';
    const isHighlightedClass = this.highlighted ? 'sr-highlighted' : '';

    return [baseClass, hideEssenceClass, isInCartClass, isHighlightedClass].join(' ');
  }

  get cartItem(): CartItem {
    const unit = this.currentUnit;
    return CartItem.buildWithUnit(
        this.sku,
        this.currentAmount,
        unit
    );
  }

  get orderTemplateChanged() {
    return this.orderTemplateItem && (this.orderTemplateItem.amount !== this.currentAmount
        || this.currentUnit.abbreviation !== this.orderTemplateItem.unit);
  }

  get isConvenienceProduct() {
    return this.virtualCategories.some((virtualCategory) => virtualCategory === CONVI_KEY);
  }

  public get isZeroAmountAllowed() {
    return this.isOrderTemplateItem() && !this.inCart;
  }

  get detailUrl(): string {
    return LinkUtil.getProductDetailUrl(this.productName, this.sku);
  }

  get shouldDisplayCartMessage(): boolean {
    return this.amountHasChanged
        || this.unitHasChanged
        || this.orderTemplateItemHasChanged
        || !this.isPurchasable
        || this.isConvenienceProduct;
  }

  get isPurchasable(): boolean {
    return this.availability === ProductDto.AvailabilityEnum.ACTIVE;
  }

  get showSelectors(): boolean {
    return this.isLoggedIn && this.isPurchasable;
  }

  @Prop({required: true})
  public sku: string;
  @Prop({required: true})
  public minSaleQuantity: number;
  @Prop({required: false, default: () => []})
  public bulkPrices: PriceDto[];
  @Prop({required: true})
  public units: UnitDefinition[];
  @Prop({required: true})
  public attributes: ProductAttributeInfo[];
  @Prop({required: true, default: () => []})
  public conditions: SalesConditionInfo[];
  @Prop()
  public netFillQuantity: ProductNetFillQuantity;
  @Prop()
  public basePrice: ProductBasePrice;
  @Prop({required: false, default: null})
  public orderTemplateItem?: OrderTemplateItemDto;
  @Prop({required: false, default: null})
  public orderTemplateId: string;
  @Prop({required: false, default: () => []})
  public orderTemplates: OrderTemplateDto[];
  @Prop()
  public imagePath: string;
  @Prop()
  public productName: string;
  @Prop()
  public productDescription: string;
  @Prop({required: false, default: () => []})
  public essences: EssenceItemDto[];
  @Prop()
  public isEssential: boolean;
  @Prop()
  public showEssenceButton: boolean;
  @Prop({required: false, default: false})
  public isPromoted: boolean;
  @Prop({required: false, default: true})
  public showLabelPrint: boolean;
  @Prop({required: false, default: true})
  public showOrderTemplates: boolean;
  @Prop()
  public highlighted: boolean;
  @Prop({required: false, default: () => []})
  public naras: NaraDto[];
  @Prop({required: true})
  public virtualCategories: string[];
  @Prop({required: false, default: ProductDto.AvailabilityEnum.ACTIVE})
  public availability: ProductDto.AvailabilityEnum;

  @cart.State('cart')
  public cart: Cart;
  public inCart = false;

  @Inject({default: MatomoContext.default()})
  public matomoContext: MatomoContext;

  public currentAmount = Math.max(this.minSaleQuantity, 1);
  public unitHasChanged = false;
  public amountHasChanged = false;
  public orderTemplateItemHasChanged = false;

  private lockService: LockService;
  private unitWatcher;
  private amountWatcher;
  private isDeleted = false;
  private deletePending = false;

  private orderTemplateApiService = ApiService.wrap(OrderTemplatesApi);
  private cartService = DependencyService.getCartService();

  public isOrderTemplateItem() {
    return this.orderTemplateId !== undefined && this.orderTemplateId !== null;
  }

  public getMinimumSalesQuantity() {
    if (this.isZeroAmountAllowed) {
      return 0;
    }
    return this.minSaleQuantity / this.currentUnit.ratio;
  }

  @Watch('cart')
  public onCartChange() {
    if (!this.cart) {
      return;
    }

    if (this.unitWatcher) {
      this.unitWatcher();
    }
    if (this.amountWatcher) {
      this.amountWatcher();
    }

    const existingItem = this.cartService.getItem(this.sku);
    if (existingItem) {
      this.currentAmount = existingItem.amount;
      this.currentUnit = this.units.find((unit) => unit.abbreviation === existingItem.unitAbbreviation);
      this.inCart = true; // set last to avoid change handlers saving the item
      this.unitWatcher = this.$watch('currentUnit', this.onUnitChange);
      this.amountWatcher = this.$watch('currentAmount', this.onAmountChange);
    } else {
      this.deletePending = false;
      this.inCart = false;
    }
  }

  public onAmountChange(newValue, oldValue) {
    this.putItemIfChanged(newValue, oldValue);
    if (!this.amountHasChanged && this.inCart) {
      this.amountHasChanged = true;
      setTimeout(() => {
        this.amountHasChanged = false;
      }, 3000);
    }
  }

  public onUnitChange(newValue, oldValue) {
    MatomoEvents.forProductListItem.unitChange.with(this.matomoContext).publishWithCartInformation(this.inCart);
    if (this.currentAmount * newValue.ratio < this.minSaleQuantity) {
      this.currentAmount = Math.max(Math.ceil(this.minSaleQuantity / newValue.ratio), 1);
    }
    this.putItemIfChanged(newValue, oldValue);
    if (!this.unitHasChanged && this.inCart) {
      this.unitHasChanged = true;
      setTimeout(() => {
        this.unitHasChanged = false;
      }, 3000);
    }
  }

  public addToCart() {
    if (!this.isValidAmount(this.cartItem.amount) || this.cartItem.amount <= 0) {
      const minQuantity = Math.max(Math.ceil(this.getMinimumSalesQuantity()), 1);
      ApiService.defaultInfoHandler(this.$t('searchResult.resultShoppingCartControl.invalidAmountForProduct',
          {sku: this.sku, productName: this.productName, minQty: minQuantity}).toString()
      );
      return;
    }
    MatomoEvents.forProductListItem.addItem.publishWithContext(this.matomoContext);
    this.cartService.putItem(this.cartItem);
  }

  public addToLocalCart() {
    if (this.currentAmount >= 1) {
      this.cartService.putLocalItem(this.cartItem);
    }
  }

  public dropAmountToZero() {
    if (!this.inCart) {
      this.currentAmount = 0;
    }
  }

  public removeFromCart() {
    if (this.inCart && !this.deletePending) {
      MatomoEvents.forProductListItem.removeItem.publishWithContext(this.matomoContext);
      this.deletePending = true;
      this.cartService.removeItem(this.cartItem);
    }
  }

  public mounted() {
    super.mounted();
    this.lockService = new LockService(this.beforeUnload.bind(this), 1000);
    if (this.orderTemplateItem != null) {
      this.currentAmount = this.orderTemplateItem.amount;
      this.currentUnit = this.getUnitForOrderTemplate();
    } else {
      this.currentUnit = this.defaultUnit;
      this.currentAmount = Math.max(Math.ceil(this.minSaleQuantity / this.currentUnit.ratio), 1);
    }
    this.$events.$on(CartEvents.CART_UPDATE, this.onCartChange);
    this.onCartChange();
    this.$events.$on(CartEvents.ADD_ALL_TO_CART, () => {
      this.addToLocalCart();
    });

    this.$events.$on('drop-order-template-items-amount', () => {
      this.dropAmountToZero();
    });
    this.$events.$on(OrderTemplateEvents.productRemoved, this.onArticleRemovedFromTemplate);
  }

  public beforeUnload() {
    this.cartService.putItem(this.cartItem, true);
  }

  public getUnitForOrderTemplate() {
    if (this.orderTemplateItem.unit) {
      return this.units.find((unit) => unit.abbreviation === this.orderTemplateItem.unit);
    } else {
      return this.visibleUnits[0];
    }
  }

  public isValidAmount(value) {
    const minimumAllowedAmount = this.isZeroAmountAllowed ? 0 : 1;
    return !(value < minimumAllowedAmount || isNaN(Number(value)) || value.toString() === '');
  }

  private setCurrentAmount(argument) {
    MatomoEvents.forProductListItem.amountChange.with(this.matomoContext).publishWithCartInformation(this.inCart);
    if (this.isValidAmount(argument)) {
      this.currentAmount = argument;
    }
  }

  private putItemIfChanged(newValue, oldValue) {
    if (newValue !== oldValue && this.inCart) {
      this.cartService.putItemWithLock(this.cartItem, this.lockService);
    }
  }

  private removeFromOrderTemplate() {
    this.orderTemplateApiService.removeOrderTemplateItem(
        ConfigService.getTenant(),
        Number(this.orderTemplateId),
        this.getOrderTemplateItem(this.sku, this.currentAmount, this.currentUnit),
        ConfigService.currentCustomerId
    ).catch(ApiService.defaultErrorHandler);
    MatomoEvents.forProductListItem.removeFromTemplate.publishWithContext(this.matomoContext);
    this.onOrderTemplateChange();
    this.isDeleted = true;
  }

  private async restoreIntoOrderTemplate() {
    try {
      await this.orderTemplateApiService.addOrderTemplateItem(
          ConfigService.getTenant(),
          Number(this.orderTemplateId),
          this.getOrderTemplateItem(this.sku, this.currentAmount, this.currentUnit),
          ConfigService.currentCustomerId
      );

      this.orderTemplateItem = this.getOrderTemplateItem(this.sku, this.currentAmount, this.currentUnit);
    } catch (e) {
      await ApiService.defaultErrorHandler(e);
    }
    MatomoEvents.forProductListItem.restoreToTemplate.publishWithContext(this.matomoContext);
    this.onOrderTemplateChange();
    this.isDeleted = false;
  }

  private submitOrderTemplateChange() {
    this.orderTemplateApiService.addOrderTemplateItem(
        ConfigService.getTenant(),
        Number(this.orderTemplateId),
        this.getOrderTemplateItem(this.sku, this.currentAmount, this.currentUnit),
        ConfigService.currentCustomerId).then((item) =>
        this.orderTemplateItem = this.getOrderTemplateItem(this.sku, this.currentAmount, this.currentUnit))
        .catch(ApiService.defaultErrorHandler);
    MatomoEvents.forProductListItem.templateChange.publishWithContext(this.matomoContext);
    this.onOrderTemplateChange();
  }

  private onOrderTemplateChange() {
    if (!this.orderTemplateItemHasChanged) {
      this.orderTemplateItemHasChanged = true;
      setTimeout(() => {
        this.orderTemplateItemHasChanged = false;
      }, 3000);
    }
  }

  private onArticleRemovedFromTemplate(eventData) {
    if (this.orderTemplateId != null &&
        this.orderTemplateId.toString() === eventData.orderTemplateId.toString() &&
        this.sku.toString() === eventData.productSku.toString()) {
      this.onOrderTemplateChange();
      this.isDeleted = true;
    }
  }

  private getOrderTemplateItem(sku, quantity, unit) {
    return {
      productSku: sku,
      amount: quantity,
      unit: unit.abbreviation
    };
  }
}
